import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { BASE_FONT_SIZE } from 'HubStyleTokens/sizes';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import UITextInput from 'UIComponents/input/UITextInput';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UISelect from 'UIComponents/input/UISelect';
import UICheckbox from 'UIComponents/input/UICheckbox'; // @ts-expect-error untyped dependency

import UIAvatar from 'ui-addon-avatars/UIAvatar';
import UILink from 'UIComponents/link/UILink';
import UIFlex from 'UIComponents/layout/UIFlex';
import H4 from 'UIComponents/elements/headings/H4';
import Small from 'UIComponents/elements/Small';
import ReviewWizardGrid from './ReviewWizardGrid';
import { getEditAvatarUrl } from '../../utils/urls';
import { getIndustryOptions, getEmployeesOptions } from '../../utils/options';
const AVATAR_SIZE = 48;
const INPUT_WIDTH = 360;
export default function BaseAboutWizardStep({
  checkedLegalDisclaimer,
  displayName,
  onUpdate,
  review,
  userEmail,
  fetchAvatars
}) {
  const industryOptions = useMemo(getIndustryOptions, []);
  const employeeOptions = useMemo(getEmployeesOptions, []);
  const avatarInput = useMemo(() => /*#__PURE__*/_jsx(UIFormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-reviews.wizard.about.avatar.label"
    }),
    children: /*#__PURE__*/_jsxs(UIToggleGroup, {
      name: "showAvatar",
      children: [/*#__PURE__*/_jsx(UIRadioInput, {
        className: "m-y-2",
        alignment: "center",
        checked: review.get('showAvatar'),
        onChange: () => onUpdate({
          showAvatar: true
        }),
        children: /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          children: [/*#__PURE__*/_jsx("div", {
            "data-test-id": "wizard-user-avatar",
            style: {
              width: AVATAR_SIZE
            },
            children: /*#__PURE__*/_jsx(UIAvatar, {
              fetchAvatars: fetchAvatars,
              portalId: PortalIdParser.get() || undefined,
              hubSpotUserEmail: userEmail,
              size: "responsive"
            })
          }), /*#__PURE__*/_jsx(Small, {
            use: "help",
            className: "m-left-3",
            children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
              message: "marketplace-ui-reviews.wizard.about.avatar.userAvatar_jsx",
              elements: {
                Link: UILink
              },
              options: {
                href: getEditAvatarUrl(),
                external: true,
                className: 'm-left-1'
              }
            })
          })]
        })
      }), /*#__PURE__*/_jsx(UIRadioInput, {
        alignment: "center",
        checked: !review.get('showAvatar'),
        onChange: () => onUpdate({
          showAvatar: false
        }),
        children: /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          children: [/*#__PURE__*/_jsx("div", {
            "data-test-id": "wizard-hide-avatar-radio-input",
            style: {
              width: AVATAR_SIZE
            },
            children: /*#__PURE__*/_jsx(UIAvatar, {
              type: "contact",
              size: "responsive"
            })
          }), /*#__PURE__*/_jsx(Small, {
            use: "help",
            className: "m-left-3",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-ui-reviews.wizard.about.avatar.noAvatar"
            })
          })]
        })
      })]
    })
  }), [fetchAvatars, onUpdate, review, userEmail]);
  const formFields = useMemo(() => /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UIFormControl, {
      required: true,
      style: {
        width: INPUT_WIDTH
      },
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.wizard.about.industry"
      }),
      children: /*#__PURE__*/_jsx(UISelect, {
        "data-test-id": "wizard-industry-select",
        onChange: ({
          target: {
            value
          }
        }) => onUpdate({
          industry: value
        }),
        options: industryOptions,
        searchable: true,
        value: review.get('industry')
      })
    }), /*#__PURE__*/_jsx(UIFormControl, {
      required: true,
      style: {
        width: INPUT_WIDTH
      },
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.wizard.about.employees"
      }),
      children: /*#__PURE__*/_jsx(UISelect, {
        "data-test-id": "wizard-employees-select",
        onChange: ({
          target: {
            value
          }
        }) => onUpdate({
          employees: value
        }),
        options: employeeOptions,
        searchable: false,
        value: review.get('employees')
      })
    })]
  }), [onUpdate, review, industryOptions, employeeOptions]);
  return /*#__PURE__*/_jsxs(ReviewWizardGrid, {
    children: [/*#__PURE__*/_jsx(H4, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.wizard.about.header"
      })
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-reviews.wizard.about.subheader"
    }), /*#__PURE__*/_jsxs(UIForm, {
      className: "m-top-2 m-bottom-4",
      children: [avatarInput, /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.wizard.about.name"
        }),
        children: /*#__PURE__*/_jsx(UITextInput, {
          style: {
            width: INPUT_WIDTH
          },
          value: displayName,
          disabled: true
        })
      }), formFields]
    }), /*#__PURE__*/_jsx(UICheckbox, {
      "data-test-id": "wizard-legal-disclaimer-checkbox",
      checked: checkedLegalDisclaimer,
      onChange: ({
        target: {
          checked
        }
      }) => onUpdate({
        checkedLegalDisclaimer: checked
      }),
      children: /*#__PURE__*/_jsx("span", {
        style: {
          fontSize: BASE_FONT_SIZE
        },
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "marketplace-ui-reviews.wizard.about.legal_jsx",
          elements: {
            Link: UILink
          },
          options: {
            href: 'https://legal.hubspot.com/review-guidelines',
            external: true
          }
        })
      })
    })]
  });
}